import { type FC, useEffect, useMemo, useState } from 'react'
import { useTheme } from 'styled-components'

import { VipSectionContainer } from '@/components/vip/shared/VipSectionContainer'
import { AutocanFeatureBadge } from '@/components/vip/vip-autocan-features/AutocanFeatureBadge'
import { AutoCanFeaturesContainer } from '@/components/vip/vip-autocan-features/styled'
import { type AutocanFeatures } from '@/components/vip/vip-autocan-features/utils'
import { isAutocanValueEstimateAvailable } from '@/domain/autocanada/isAutocanValueEstimateAvailable'
import { isAutocanWarrantyAvailable } from '@/domain/autocanada/isAutocanWarrantyAvailable'
import { getLocationPath } from '@/domain/location/getLocationPath'
import { type VipQueryListing } from '@/types/listings'

type AutoCanFeaturesSectionProps = {
  isDealerListing: boolean
  listing: VipQueryListing
  userAgent: string
}

export const AutoCanFeaturesSection: FC<AutoCanFeaturesSectionProps> = ({
  listing,
  isDealerListing,
  userAgent,
}) => {
  const [allowedFeatures, setAllowedFeatures] = useState<AutocanFeatures[]>([])
  const { spacing } = useTheme()

  const locationPath = useMemo(() => getLocationPath(listing.location.id), [listing.location.id])

  useEffect(() => {
    const allowWarranty = isAutocanWarrantyAvailable({
      categoryId: listing.categoryId,
      isDealerListing,
      locationPath,
      sellerType: listing.posterInfo.sellerType,
    })

    const allowValueEstimate = isAutocanValueEstimateAvailable({
      categoryId: listing.categoryId,
      isDealerListing,
      locationPath,
      sellerType: listing.posterInfo.sellerType,
    })

    const allowedFeatures: AutocanFeatures[] = []

    if (allowWarranty) allowedFeatures.push('warranty')
    if (allowValueEstimate) allowedFeatures.push('value-estimate')

    setAllowedFeatures(allowedFeatures)
  }, [isDealerListing, listing.categoryId, listing.posterInfo.sellerType, locationPath])

  /**
   * We will account how many live features autocan has
   * It will switch between showing the badge as a inline tile with another autocan feature AND
   * taking the full row for a single available feature
   */
  const autocanLiveFeatures = allowedFeatures.length
  const useInlineVariant = autocanLiveFeatures > 1

  if (autocanLiveFeatures === 0) return null

  return (
    <VipSectionContainer pTop={spacing.large} pBottom={spacing.large}>
      <AutoCanFeaturesContainer useInlineVariant={useInlineVariant}>
        {allowedFeatures.includes('warranty') && (
          <AutocanFeatureBadge
            useInlineVariant={useInlineVariant}
            listing={listing}
            userAgent={userAgent}
            variant="warranty"
          />
        )}

        {allowedFeatures.includes('value-estimate') && (
          <AutocanFeatureBadge
            useInlineVariant={useInlineVariant}
            listing={listing}
            userAgent={userAgent}
            variant="value-estimate"
          />
        )}
      </AutoCanFeaturesContainer>
    </VipSectionContainer>
  )
}
